import { FC } from 'react';
import { ArrowDownIcon, IconWeight } from '@phosphor-icons/react';
import cn from 'classnames';
import { trackOutfittingAnchorClick } from '_utils/GA4/trackOutfittingAnchorEvents';

import { phosphorIcons } from './phosphorIconList';
import styles from './OutfittingAnchor.module.scss';

const ICON_SIZE = 24;
const DEFAULT_ICON_NAME = 'TShirtIcon-regular';
const DEFAULT_PRODUCT_NAME_TOKEN = '{{productName}}';

// Phosphor icon names are stored as "<IconName>-<weight>", e.g. "TShirtIcon-fill".
const ICON_NAME_PATTERN = /^(.+)-(thin|light|regular|fill|duotone|bold)$/;

export interface OutfittingAnchorProps {
  /** Localized heading. Configured by the digi merch team in Prismic. */
  heading: string;
  /**
   * Localized body copy. Configured in Prismic and may contain a product-name
   * token (see `productNameToken`) that is swapped for `productName`.
   */
  body: string;
  /**
   * Phosphor icon name in "<IconName>-<weight>" form (e.g. "TShirtIcon-regular"),
   * configured in Prismic per category / department / brand. Mirrors the
   * BannerStrip pattern; only icons in `phosphorIconList` are available.
   */
  iconName?: string;
  /** Product name injected into `body` wherever `productNameToken` appears. */
  productName?: string;
  /** Token in `body` replaced with `productName`. Defaults to `{{productName}}`. */
  productNameToken?: string;
  /**
   * Disabled on initial load until the outfitting fetch resolves; the PDP then
   * enables or removes the anchor depending on whether the tile exists.
   */
  disabled?: boolean;
  /** Invoked on select — wired by the PDP to smooth-scroll to the Outfitting Tile. */
  onSelect?: () => void;
  className?: string;
  'data-testid'?: string;
}

const injectProductName = (
  body: string,
  productName?: string,
  token: string = DEFAULT_PRODUCT_NAME_TOKEN
): string => {
  if (!productName) return body;
  return body.split(token).join(productName);
};

const resolveIcon = (iconName: string) => {
  const match = iconName.match(ICON_NAME_PATTERN);
  const name = match ? match[1] : iconName;
  const weight = (match?.[2] ?? 'regular') as IconWeight;
  const IconComponent = phosphorIcons[name as keyof typeof phosphorIcons];
  if (!IconComponent) return null;
  return <IconComponent size={ICON_SIZE} weight={weight} aria-hidden />;
};

/**
 * Outfitting Anchor — a non-navigating CTA on the PDP that anchors the shopper
 * to the Outfitting Tile further down the page. Data fetching, Prismic config,
 * and the scroll wiring are owned by the PDP; the anchor owns only its own
 * click analytics (see `trackOutfittingAnchorClick`).
 */
const OutfittingAnchor: FC<OutfittingAnchorProps> = ({
  heading,
  body,
  iconName = DEFAULT_ICON_NAME,
  productName,
  productNameToken,
  disabled = false,
  onSelect,
  className,
  'data-testid': dataTestId,
}) => {
  const bodyText = injectProductName(body, productName, productNameToken);
  const icon = resolveIcon(iconName);

  const handleClick = () => {
    trackOutfittingAnchorClick({
      data: {
        // Read at click time rather than from the router so the path reflects
        // the variant the shopper was actually viewing (colour lives in the query).
        interactionLocation: window.location.pathname + window.location.search,
        linkText: heading,
      },
    });
    onSelect?.();
  };

  return (
    <button
      type="button"
      className={cn(styles.anchor, className)}
      onClick={handleClick}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {icon && (
        <span className={styles.icon} aria-hidden>
          {icon}
        </span>
      )}
      <span className={styles.content}>
        <span className={styles.heading}>{heading}</span>
        <span className={styles.body}>{bodyText}</span>
      </span>
      <ArrowDownIcon className={styles.arrow} size={20} aria-hidden />
    </button>
  );
};

export default OutfittingAnchor;
